import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Users, Send, MessageCircle, Wifi, WifiOff } from 'lucide-react';
import { format } from 'date-fns';

const COLORS = [
  '#3B82F6', '#EC4899', '#8B5CF6', '#F59E0B', '#10B981',
  '#EF4444', '#06B6D4', '#F97316', '#6366F1', '#84CC16',
];

export const CollaborationPanel = ({ postId, userId, userName }) => {
  const [connected, setConnected] = useState(false);
  const [users, setUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const wsRef = useRef(null);
  const commentsEndRef = useRef(null);
  const reconnectTimeout = useRef(null);

  const getBackendWsUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    return `${wsUrl}/api/collaboration/ws/${postId}`;
  };

  const connectWs = useCallback(() => {
    if (!postId || !userId) return;

    try {
      const ws = new WebSocket(getBackendWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'join',
          user_id: userId,
          user_name: userName || 'Anonymous',
        }));
        setConnected(true);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'presence':
            setUsers(data.users || []);
            break;
          case 'typing':
            setTypingUsers(prev => {
              if (data.is_typing) {
                return [...prev.filter(u => u.user_id !== data.user_id), {
                  user_id: data.user_id,
                  user_name: data.user_name,
                }];
              }
              return prev.filter(u => u.user_id !== data.user_id);
            });
            break;
          case 'comment':
            setComments(prev => [...prev, data]);
            break;
          case 'user_joined':
            break;
          case 'user_left':
            setTypingUsers(prev => prev.filter(u => u.user_id !== data.user_id));
            break;
          default:
            break;
        }
      };

      ws.onclose = () => {
        setConnected(false);
        reconnectTimeout.current = setTimeout(connectWs, 3000);
      };

      ws.onerror = () => {
        setConnected(false);
      };
    } catch (e) {
      setConnected(false);
    }
  }, [postId, userId, userName]);

  useEffect(() => {
    connectWs();

    // Load existing comments
    const loadComments = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/collaboration/comments/${postId}`);
        if (res.ok) {
          const data = await res.json();
          setComments(data);
        }
      } catch (e) { /* silent */ }
    };
    if (postId) loadComments();

    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connectWs, postId]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const sendTypingStart = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing_start' }));
    }
  };

  const sendTypingStop = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing_stop' }));
    }
  };

  const sendContentUpdate = (content, cursorPosition) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'content_update',
        content,
        cursor_position: cursorPosition,
      }));
    }
  };

  const sendComment = () => {
    if (!commentInput.trim() || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'comment',
      text: commentInput.trim(),
    }));
    setCommentInput('');
  };

  const getUserColor = (uid) => {
    const index = uid ? uid.charCodeAt(0) % COLORS.length : 0;
    return COLORS[index];
  };

  if (collapsed) {
    return (
      <div
        className="fixed bottom-6 right-6 z-50 cursor-pointer"
        onClick={() => setCollapsed(false)}
        data-testid="collab-expand-btn"
      >
        <div className="bg-white rounded-full shadow-lg border p-3 flex items-center gap-2 hover:shadow-xl transition-shadow">
          <Users className="h-5 w-5 text-emerald-600" />
          {users.length > 1 && (
            <Badge className="bg-emerald-500 text-white text-xs">{users.length}</Badge>
          )}
          {connected ? (
            <Wifi className="h-3 w-3 text-emerald-500" />
          ) : (
            <WifiOff className="h-3 w-3 text-slate-400" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border-l border-slate-200 bg-white w-72 flex flex-col" data-testid="collaboration-panel">
      {/* Header */}
      <div className="p-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-medium text-slate-800">Collaboration</span>
          {connected ? (
            <span className="w-2 h-2 rounded-full bg-emerald-500" title="Connected" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-slate-300" title="Disconnected" />
          )}
        </div>
        <button
          onClick={() => setCollapsed(true)}
          className="text-slate-400 hover:text-slate-600 text-xs"
          data-testid="collab-collapse-btn"
        >
          Minimize
        </button>
      </div>

      {/* Active Users */}
      <div className="p-3 border-b border-slate-100">
        <p className="text-xs font-medium text-slate-500 mb-2">
          {users.length} {users.length === 1 ? 'person' : 'people'} editing
        </p>
        <div className="flex flex-wrap gap-1">
          {users.map((u) => (
            <div
              key={u.user_id}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
              style={{ backgroundColor: `${getUserColor(u.user_id)}15`, color: getUserColor(u.user_id) }}
            >
              <span
                className="w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-bold"
                style={{ backgroundColor: getUserColor(u.user_id) }}
              >
                {(u.name || 'A')[0].toUpperCase()}
              </span>
              <span className="font-medium">{u.user_id === userId ? 'You' : u.name}</span>
            </div>
          ))}
        </div>

        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <p className="text-xs text-slate-400 mt-2 italic animate-pulse">
            {typingUsers.map(u => u.user_name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </p>
        )}
      </div>

      {/* Comments */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
        <p className="text-xs font-medium text-slate-500 mb-1">
          <MessageCircle className="h-3 w-3 inline mr-1" />
          Comments ({comments.length})
        </p>
        {comments.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No comments yet</p>
        ) : (
          comments.map((c, i) => (
            <div key={i} className="bg-slate-50 rounded-lg p-2">
              <div className="flex items-center gap-1 mb-1">
                <span
                  className="w-4 h-4 rounded-full text-white flex items-center justify-center text-[8px] font-bold"
                  style={{ backgroundColor: getUserColor(c.user_id) }}
                >
                  {(c.user_name || 'A')[0].toUpperCase()}
                </span>
                <span className="text-xs font-medium text-slate-700">
                  {c.user_id === userId ? 'You' : c.user_name}
                </span>
                <span className="text-[10px] text-slate-400 ml-auto">
                  {c.timestamp ? format(new Date(c.timestamp), 'HH:mm') : ''}
                </span>
              </div>
              <p className="text-xs text-slate-600">{c.text}</p>
            </div>
          ))
        )}
        <div ref={commentsEndRef} />
      </div>

      {/* Comment Input */}
      <div className="p-3 border-t border-slate-200">
        <div className="flex gap-2">
          <Input
            placeholder="Add a comment..."
            value={commentInput}
            onChange={(e) => setCommentInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendComment()}
            className="text-xs h-8"
            data-testid="collab-comment-input"
          />
          <Button
            size="sm"
            className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600"
            onClick={sendComment}
            disabled={!commentInput.trim()}
            data-testid="collab-send-comment-btn"
          >
            <Send className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CollaborationPanel;
